import React, { useState, useEffect } from "react";
import { Layout, Menu, Button, Drawer, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AppstoreOutlined,
  HomeOutlined,
  RobotOutlined,
  IdcardOutlined,
  DeploymentUnitOutlined,
  AppstoreAddOutlined,
  BellOutlined,
  ThunderboltOutlined,
  ApiOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAI } from "../../context/AIContext";
import { useTheme } from "../../context/ThemeContext";
import "./global.css";
import "./sidebar.css";

const { Sider } = Layout;

const sidebarIconClass = (active, isDark) =>
  `sidebar-ant-icon ${active ? "sidebar-icon-active" : (isDark ? "sidebar-icon-inactive-dark" : "sidebar-icon-inactive")}`;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeKeyRaw = location.pathname.replace("/", "") || "dashboard";
  const activeKey = activeKeyRaw.startsWith("applications")
    ? "applications"
    : activeKeyRaw;
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeKeyColor, setActiveKeyColor] = useState("");
  const [showRightPanel, setShowRightPanel] = useState(false); // NEW: Right panel state
  const { badgeVisible, clearBadge } = useAI();
  const { isDark } = useTheme();

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!isMobile && mobile) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, [isMobile]);

  const renderLabel = (text) => text;

  const menuItems = [
    {
      key: "dashboard",
      className: activeKey === "dashboard" ? "active-menu-item" : "",
      icon: <HomeOutlined className={sidebarIconClass(activeKey === "dashboard", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Home", "Overview and key metrics at a glance"),
      onClick: () => {
        setActiveKeyColor("dashboard");
        setShowRightPanel(false);
        navigate("/dashboard");
        if (isMobile) setDrawerVisible(false);
      },
    },
    {
      key: "aiDebugger",
      className: activeKey === "aiDebugger" ? "active-menu-item" : "",
      icon: (
        <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <RobotOutlined className={sidebarIconClass(activeKey === "aiDebugger", isDark)} style={{ fontSize: 20 }} />
          {badgeVisible && (
            <span className="sidebar-ai-badge" aria-hidden />
          )}
        </div>
      ),
      label: renderLabel("Clairai-Copilot", "AI-powered assistant and debugging"),
      onClick: () => {
        setActiveKeyColor("aiDebugger");
        setShowRightPanel(false);
        navigate("/aiDebugger");
        clearBadge();
        if (isMobile) setDrawerVisible(false);
      },
    },
    {
      key: "accounts",
      className: activeKey === "accounts" ? "active-menu-item" : "",
      icon: <IdcardOutlined className={sidebarIconClass(activeKey === "accounts", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Accounts", "Manage accounts and access"),
      onClick: () => {
        setActiveKeyColor("accounts");
        navigate("/accounts");
        if (isMobile) setDrawerVisible(false);
      },
    },
    {
      key: "pipeline-discovery",
      className: activeKey === "pipeline-discovery" ? "active-menu-item" : "",
      icon: <DeploymentUnitOutlined className={sidebarIconClass(activeKey === "pipeline-discovery", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Pipeline Discovery", "Discover and map data pipelines"),
      onClick: () => {
        setActiveKeyColor("pipeline-discovery");
        navigate("/pipeline-discovery");
        if (isMobile) setDrawerVisible(false);
      },
    },
    {
      key: "applications",
      className: activeKey === "applications" ? "active-menu-item" : "",
      icon: <AppstoreAddOutlined className={sidebarIconClass(activeKey === "applications", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Applications", "Discover and onboard applications"),
      onClick: () => {
        setActiveKeyColor("applications");
        navigate("/applications");
        if (isMobile) setDrawerVisible(false);
      },
    },
    // {
    //   key: "workflows",
    //   className: activeKey === "workflows" || activeKey === "workflow-analyzer" ? "active-menu-item" : "",
    //   icon: <PartitionOutlined className={sidebarIconClass(activeKey === "workflows" || activeKey === "workflow-analyzer", isDark)} style={{ fontSize: 20 }} />,
    //   label: renderLabel("Workflow Builder", "Build and visualize pipeline dependencies"),
    //   onClick: () => {
    //     setActiveKeyColor("workflows");
    //     navigate("/workflows");
    //     if (isMobile) setDrawerVisible(false);
    //   },
    // },
    // {
    //   key: "metrics",
    //   className: activeKey === "metrics" ? "active-menu-item" : "",
    //   icon: <LineChartOutlined className={sidebarIconClass(activeKey === "metrics", isDark)} style={{ fontSize: 20 }} />,
    //   label: renderLabel("Metrics", "Query and visualize metrics"),
    //   onClick: () => {
    //     setActiveKeyColor("metrics");
    //     navigate("/metrics");
    //     if (isMobile) setDrawerVisible(false);
    //   },
    // },
    // {
    //   key: "dashboards",
    //   className: activeKey === "dashboards" || activeKey === "graphs-test" ? "active-menu-item" : "",
    //   icon: <DashboardOutlined className={sidebarIconClass(activeKey === "dashboards" || activeKey === "graphs-test", isDark)} style={{ fontSize: 20 }} />,
    //   label: renderLabel("Dashboards (Beta)", "Build and view custom dashboards"),
    //   onClick: () => {
    //     setActiveKeyColor("dashboards");
    //     navigate("/dashboards");
    //     if (isMobile) setDrawerVisible(false);
    //   },
    // },
    // {
    //   key: "logs",
    //   className: activeKey === "logs" ? "active-menu-item" : "",
    //   icon: <FileTextOutlined className={sidebarIconClass(activeKey === "logs", isDark)} style={{ fontSize: 20 }} />,
    //   label: renderLabel("Logs", "Search and explore log data"),
    //   onClick: () => {
    //     setActiveKeyColor("logs");
    //     navigate("/logs");
    //     if (isMobile) setDrawerVisible(false);
    //   },
    // },
    {
      key: "alerts",
      className: activeKey === "alerts" ? "active-menu-item" : "",
      icon: <BellOutlined className={sidebarIconClass(activeKey === "alerts", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Alerts", "Manage alert rules and firing alerts"),
      onClick: () => {
        setActiveKeyColor("alerts");
        navigate("/alerts");
        if (isMobile) setDrawerVisible(false);
      },
    },
    {
      key: "incidents",
      className: activeKey === "incidents" ? "active-menu-item" : "",
      icon: <ThunderboltOutlined className={sidebarIconClass(activeKey === "incidents", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Incidents", "Auto-RCA reports and incident history"),
      onClick: () => {
        setActiveKeyColor("incidents");
        navigate("/incidents");
        if (isMobile) setDrawerVisible(false);
      },
    },
    {
      key: "integrations/connectors",
      className: activeKey === "integrations/connectors" ? "active-menu-item" : "",
      icon: <ApiOutlined className={sidebarIconClass(activeKey === "integrations/connectors", isDark)} style={{ fontSize: 20 }} />,
      label: renderLabel("Connectors", "Jira, GitHub, Loki, Prometheus integrations"),
      onClick: () => {
        setActiveKeyColor("integrations/connectors");
        navigate("/integrations/connectors");
        if (isMobile) setDrawerVisible(false);
      },
    },
    // {
    //   key: "active-services",
    //   className: activeKey === "active-services" ? "active-menu-item" : "",
    //   icon: <ApartmentOutlined className={sidebarIconClass(activeKey === "active-services", isDark)} style={{ fontSize: 20 }} />,
    //   label: renderLabel("Traces", "Active services and distributed traces"),
    //   onClick: () => {
    //     setActiveKeyColor("active-services");
    //     navigate("/active-services");
    //     if (isMobile) setDrawerVisible(false);
    //   },
    // },
  ];

  const primaryItems = menuItems.slice(0, 2); // Home, AI Assistant
  const moreItems = menuItems.slice(2); // Rest of items

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          onClick={() => setDrawerVisible(true)}
          className="sidebar-mobile-menu-button"
        />
        <Drawer
          placement="left"
          closable={true}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: 0 }}
          headerStyle={{ display: "none" }}
          width={280}
          rootClassName={`app-sidebar-drawer app-sidebar-drawer--${isDark ? "dark" : "light"}`}
        >
          <div className="app-sidebar-drawer__logo">
            <img
              src="/Logo/LOGO 48X48.svg"
              alt="ClairAI logo"
              className="sidebar-drawer-logo"
              width={48}
              height={48}
              decoding="async"
            />
          </div>
          <Menu
            theme={isDark ? "dark" : "light"}
            mode="inline"
            items={menuItems}
            selectedKeys={[activeKey]}
            className="app-sidebar-drawer__menu sidebar-drawer-menu"
          />
        </Drawer>
      </>
    );
  }

  return (
    <div className="app-sidebar-root" style={{ display: "flex", minHeight: "100vh", alignSelf: "stretch" }}>
      <Sider
        width={200}
        theme={isDark ? "dark" : "light"}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        collapsedWidth={80}
        trigger={null}
        className="sidebar-container"
        style={{ position: "relative", zIndex: 100, minHeight: "100vh" }}
      >
        <div className="sidebar-header">
          {collapsed ? (
            <img
              src="/Logo/LOGO 32X32.svg"
              alt="ClairAI logo"
              className="sidebar-logo-collapsed"
              width={32}
              height={32}
              decoding="async"
            />
          ) : (
            <img
              src="/Logo/LOGO 180X180.svg"
              alt="ClairAI logo"
              className="sidebar-logo-expanded"
              width={180}
              height={180}
              decoding="async"
              fetchpriority="high"
            />
          )}
        </div>

        <div className="sidebar-menu-container">
          <Menu
            theme={isDark ? "dark" : "light"}
            mode="inline"
            selectedKeys={[activeKey]}
          >
            {/* Primary Items */}
            {primaryItems.map((item) => (
              <Menu.Item
                key={item.key}
                className={item.className}
                icon={item.icon}
                onClick={item.onClick}
              >
                {item.label}
              </Menu.Item>
            ))}

            {/* More Button - Toggle right panel */}
            <Menu.Item
              key="more"
              icon={<AppstoreOutlined className="sidebar-more-icon" />}
              onClick={() => setShowRightPanel(!showRightPanel)}
            >
              {renderLabel("More", "Accounts, Metrics, Logs, Alerts, and more")}
            </Menu.Item>
          </Menu>
        </div>

        <div className="sidebar-collapse-container">
          <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
            <Button
              type="text"
              onClick={() => setCollapsed(!collapsed)}
              className="sidebar-collapse-button sidebar-collapse-btn"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            >
              {!collapsed && <span className="sidebar-collapse-label">Collapse</span>}
            </Button>
          </Tooltip>
        </div>
      </Sider>

      {/* RIGHT SIDEBAR PANEL - Slides in/out */}
      {showRightPanel && (
        <div className="app-sidebar__flyout">
          <header className="app-sidebar__flyout-header">
            <button
              type="button"
              className="app-sidebar__flyout-dismiss"
              onClick={() => setShowRightPanel(false)}
              aria-label="Back to main menu"
            >
              <ArrowLeftOutlined className="app-sidebar__flyout-dismiss-icon" aria-hidden />
              <span className="app-sidebar__flyout-dismiss-text">Back</span>
            </button>
          </header>

          <div className="app-sidebar__flyout-body">
            <Menu
              theme={isDark ? "dark" : "light"}
              mode="inline"
              selectedKeys={[activeKey]}
              className="app-sidebar__flyout-menu"
              style={{ borderRight: "none" }}
            >
              {moreItems.map((item) => (
                <Menu.Item
                  key={item.key}
                  className={item.className}
                  icon={item.icon}
                  onClick={() => {
                    item.onClick?.();
                    setShowRightPanel(false);
                  }}
                  title={typeof item.label === "string" ? item.label : undefined}
                >
                  {item.label}
                </Menu.Item>
              ))}
            </Menu>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
